import type {
  QueryObserverResult,
  RefetchOptions,
} from '@tanstack/react-query';
import type { TRPCClientErrorLike } from '@trpc/client';
import { isTRPCClientError } from '@trpc/client';

/**
 * tRPC twin of `isPermissionError` (src/utils/graphqlUtils.ts). The Apollo
 * path surfaces a denied action as a GraphQL error with
 * `extensions.code === 'permission-error'`; the tRPC path surfaces the same
 * denial as a `TRPCClientError` whose formatted `code` is `'FORBIDDEN'`
 * (the data-layer returns 403 for a non-author edit; the tRPC service maps
 * 403 → FORBIDDEN).
 *
 * The formatted code lives on `error.data.code` — the same accessor
 * `mapFormError` (src/components/form/form/formError.ts) reads. `error.shape`
 * carries the raw formatter output; check `shape.data.code` too so the helper
 * still fires if `data` is momentarily unpopulated.
 *
 * Used by the comments editor's `tinycomments_edit_comment` handler
 * (RSP-6496) to treat a non-author edit as `canEdit: false` under EITHER data
 * path behind the `trpc` flag.
 */
export const isTrpcPermissionError = (e: unknown): boolean => {
  if (!isTRPCClientError(e)) {
    return false;
  }

  return e.data?.code === 'FORBIDDEN' || e.shape?.data?.code === 'FORBIDDEN';
};

export const mapTRPCRefetch = async <TTRPCData, TGraphQLQuery>(
  trpcRefetch: (options?: RefetchOptions) => Promise<
    QueryObserverResult<
      TTRPCData,
      TRPCClientErrorLike<{
        transformer: true;
        errorShape: unknown;
      }>
    >
  >,
  trpcToGraphQLMapper: (data: TTRPCData) => TGraphQLQuery
) => {
  const { data: refetchData, error } = await trpcRefetch();

  const mappedRefetchData = refetchData
    ? trpcToGraphQLMapper(refetchData)
    : undefined;

  return { data: mappedRefetchData, error: error ?? null };
};
